import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

// ─── Types ────────────────────────────────────────────────────────────────────

type StageStatus = 'done' | 'active' | 'pending' | 'failed' | 'timeout';

interface PipelineLink {
  label: string;
  url: string;
}

interface PipelineStage {
  label: string;
  description: string;
  status: StageStatus;
  links?: PipelineLink[];
}

interface PrStatus {
  state: 'open' | 'closed';
  merged: boolean;
  mergedAt?: string;
  title?: string;
  workflowsUrl?: string;
}

interface RunStatus {
  status: 'queued' | 'in_progress' | 'completed';
  conclusion: 'success' | 'failure' | 'cancelled' | null;
  name: string;
  html_url: string;
}

interface ArgoStatus {
  found: boolean;
  syncStatus?: string;      // Synced | OutOfSync | Unknown
  healthStatus?: string;    // Healthy | Progressing | Degraded | Suspended | Missing | Unknown
  lastSyncTime?: string;
  operationPhase?: string;  // Succeeded | Failed | Error | Running
  appUrl?: string;
}

interface Props {
  taskStatus: string;
  prUrl?: string;
  actionsRunUrl?: string;
  entityRef?: string;
  argoAppName?: string;
  argoInstance?: string;
  // Target platform label for the final stage (e.g. "AKS", "OpenShift").
  platform?: string;
}

// ─── Design tokens (match CustomScaffolderTaskPage) ───────────────────────────

const S = {
  white:     '#ffffff',
  border:    '#e3e8ee',
  shadow:    '0 2px 5px rgba(50,50,93,0.08), 0 1px 3px rgba(0,0,0,0.05)',
  text:      '#0a2540',
  textSub:   '#425466',
  textMuted: '#8898aa',
  accent:    '#635bff',
  green:     '#1ea672',
  greenBg:   '#ecfdf5',
  greenText: '#047857',
  red:       '#e5484d',
  redBg:     '#fff0f0',
  redText:   '#b91c1c',
  amber:     '#d97706',
  amberBg:   '#fffbeb',
  amberText: '#92400e',
  blue:      '#2563eb',
  blueBg:    '#eff6ff',
  blueText:  '#1d4ed8',
  radius:    '8px',
  radiusSm:  '5px',
} as const;

// ─── Timeline node + status pill ──────────────────────────────────────────────

function StageDot({ status }: { status: StageStatus }) {
  const size = 18;
  if (status === 'active') return <CircularProgress size={size} style={{ color: S.accent }} />;
  if (status === 'done') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: S.green, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="10" height="10" viewBox="0 0 9 9" fill="none">
          <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: S.red, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="10" height="10" viewBox="0 0 9 9" fill="none">
          <path d="M2.5 2.5L6.5 6.5M6.5 2.5L2.5 6.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'timeout') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: S.amber, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff"><path d="M6 2h12v5l-4 5 4 5v5H6v-5l4-5-4-5V2z" /></svg>
      </span>
    );
  }
  return <span style={{ width: size, height: size, borderRadius: '50%', background: S.white, border: '2px solid #c8d0da', display: 'inline-block', boxSizing: 'border-box' }} />;
}

function StagePill({ status }: { status: StageStatus }) {
  const map: Record<StageStatus, { label: string; bg: string; text: string; dot: string }> = {
    done:    { label: 'Done',           bg: S.greenBg, text: S.greenText, dot: S.green },
    active:  { label: 'In progress',    bg: S.blueBg,  text: S.blueText,  dot: S.blue  },
    timeout: { label: 'Pending review', bg: S.amberBg, text: S.amberText, dot: S.amber },
    failed:  { label: 'Failed',         bg: S.redBg,   text: S.redText,   dot: S.red   },
    pending: { label: 'Pending',        bg: '#f3f4f6', text: '#6b7280',   dot: '#c0c8d2' },
  };
  const c = map[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 20,
      background: c.bg, color: c.text,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {c.label}
    </span>
  );
}

function StageLink({ link }: { link: PipelineLink }) {
  const style: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: S.radiusSm,
    border: `1px solid ${S.border}`, background: S.white, color: S.accent,
    fontSize: 12, fontWeight: 600, textDecoration: 'none', lineHeight: 1.6,
  };
  const inner = <>{link.label}<LaunchIcon style={{ fontSize: 13 }} /></>;
  return (
    <Tooltip title={link.url}>
      {link.url.startsWith('http') ? (
        <a href={link.url} target="_blank" rel="noopener noreferrer" style={style}>{inner}</a>
      ) : (
        <Link to={link.url}><span style={style}>{inner}</span></Link>
      )}
    </Tooltip>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PR_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export const DeploymentPipeline = ({ taskStatus, prUrl, actionsRunUrl, entityRef, argoAppName, argoInstance, platform = 'OpenShift' }: Props) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [prStatus, setPrStatus]         = useState<PrStatus | null>(null);
  const [runStatus, setRunStatus]       = useState<RunStatus | null>(null);
  const [argoStatus, setArgoStatus]     = useState<ArgoStatus | null>(null);
  const [prLoading, setPrLoading]       = useState(false);
  const [runLoading, setRunLoading]     = useState(false);

  // For dispatch flows: PR URL discovered by scanning workflow job logs
  const [discoveredPrUrl, setDiscoveredPrUrl] = useState<string | null | undefined>(undefined);

  // Track when PR URL was first seen so we can compute the 10-min timeout
  const prFirstSeenAt = useRef<number | null>(null);

  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  useEffect(() => {
    discoveryApi.getBaseUrl('tasks').then(url => setBaseUrl(url)).catch(() => {});
  }, [discoveryApi]);

  // ─── Effective PR URL: direct (from template) or discovered (dispatch flow) ──

  const effectivePrUrl = prUrl ?? discoveredPrUrl ?? undefined;

  // Record first time we see a PR URL
  useEffect(() => {
    if (effectivePrUrl && prFirstSeenAt.current === null) {
      prFirstSeenAt.current = Date.now();
    }
  }, [effectivePrUrl]);

  // ─── Clock tick for timeout display (every 30s while PR is open) ─────────────

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!effectivePrUrl || prStatus?.merged) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [effectivePrUrl, prStatus?.merged]);

  const prTimedOut = Boolean(
    effectivePrUrl &&
    !prStatus?.merged &&
    prFirstSeenAt.current !== null &&
    (now - prFirstSeenAt.current) >= PR_TIMEOUT_MS,
  );

  // ─── Fetch helpers ────────────────────────────────────────────────────────────

  const fetchPrStatus = useCallback(async (targetUrl: string) => {
    if (!baseUrl) return;
    setPrLoading(true);
    try {
      const resp = await fetchApi.fetch(`${baseUrl}/pr-status?url=${encodeURIComponent(targetUrl)}`);
      if (resp.ok) setPrStatus(await resp.json());
    } catch {
      // silently ignore
    } finally {
      setPrLoading(false);
    }
  }, [baseUrl, fetchApi]);

  const fetchRunStatus = useCallback(async () => {
    if (!actionsRunUrl || !baseUrl) return;
    setRunLoading(true);
    try {
      const resp = await fetchApi.fetch(`${baseUrl}/run-status?url=${encodeURIComponent(actionsRunUrl)}`);
      if (resp.ok) setRunStatus(await resp.json());
    } catch {
      // silently ignore
    } finally {
      setRunLoading(false);
    }
  }, [actionsRunUrl, baseUrl, fetchApi]);

  const fetchRunPr = useCallback(async () => {
    if (!actionsRunUrl || !baseUrl) return;
    try {
      const resp = await fetchApi.fetch(`${baseUrl}/run-pr?url=${encodeURIComponent(actionsRunUrl)}`);
      if (resp.ok) {
        const data = await resp.json();
        if (data.prUrl) {
          setDiscoveredPrUrl(data.prUrl);
        } else if (!data.runStatus) {
          setDiscoveredPrUrl(null);
        }
      }
    } catch {
      // ignore
    }
  }, [actionsRunUrl, baseUrl, fetchApi]);

  const fetchArgoStatus = useCallback(async () => {
    if (!argoAppName || !baseUrl) return;
    try {
      const params = new URLSearchParams({ appName: argoAppName });
      if (argoInstance) params.set('instance', argoInstance);
      const resp = await fetchApi.fetch(`${baseUrl}/argocd-app-status?${params}`);
      if (resp.ok) setArgoStatus(await resp.json());
    } catch {
      // ignore
    }
  }, [argoAppName, argoInstance, baseUrl, fetchApi]);

  // ─── Initial fetch ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!baseUrl) return;
    if (prUrl) fetchPrStatus(prUrl);
    fetchRunStatus();
    if (actionsRunUrl && !prUrl) fetchRunPr();
  }, [baseUrl, prUrl, actionsRunUrl, fetchPrStatus, fetchRunStatus, fetchRunPr]);

  // ─── Derived state ────────────────────────────────────────────────────────────

  const templateDone   = taskStatus === 'completed';
  const templateFailed = taskStatus === 'failed';
  const templateActive = taskStatus === 'running' || taskStatus === 'processing';

  const runCompleted = runStatus?.status === 'completed';
  const runSuccess   = runCompleted && runStatus?.conclusion === 'success';
  const runFailed    = runCompleted && (runStatus?.conclusion === 'failure' || runStatus?.conclusion === 'cancelled');
  const runInFlight  = runStatus?.status === 'in_progress' || runStatus?.status === 'queued';

  const prMerged = prStatus?.merged === true;
  const prOpen   = prStatus?.state === 'open';

  const argoSynced  = argoStatus?.found && argoStatus.syncStatus === 'Synced';
  const argoHealthy = argoStatus?.found && argoStatus.healthStatus === 'Healthy';
  const argoFailed  = argoStatus?.found && (
    argoStatus.syncStatus === 'OutOfSync' ||
    argoStatus.healthStatus === 'Degraded' ||
    argoStatus.operationPhase === 'Failed' ||
    argoStatus.operationPhase === 'Error'
  );

  // ─── Polling logic ────────────────────────────────────────────────────────────

  const needRunPoll    = Boolean(actionsRunUrl) && !runCompleted;
  const needRunPrPoll  = Boolean(actionsRunUrl) && !prUrl && runSuccess && !discoveredPrUrl;
  // Poll PR until merged (even past timeout — so UI updates if it eventually merges)
  const needPrPoll     = Boolean(effectivePrUrl) && !prMerged;
  // Poll ArgoCD after merge until synced+healthy
  const needArgoPoll   = Boolean(argoAppName) && prMerged && !(argoSynced && argoHealthy);

  useEffect(() => {
    if (!baseUrl) return;
    const fns: Array<() => void> = [];
    if (needRunPoll)              fns.push(fetchRunStatus);
    if (needRunPrPoll)            fns.push(fetchRunPr);
    if (needPrPoll && effectivePrUrl) fns.push(() => fetchPrStatus(effectivePrUrl));
    if (needArgoPoll)             fns.push(fetchArgoStatus);
    if (fns.length === 0) return;
    const id = setInterval(() => fns.forEach(f => f()), 15_000);
    return () => clearInterval(id);
  }, [
    baseUrl, needRunPoll, needRunPrPoll, needPrPoll, needArgoPoll,
    effectivePrUrl, fetchRunStatus, fetchRunPr, fetchPrStatus, fetchArgoStatus,
  ]);

  // Trigger immediate fetches on state transitions
  useEffect(() => {
    if (runSuccess && !prUrl && discoveredPrUrl === undefined) fetchRunPr();
  }, [runSuccess, prUrl, discoveredPrUrl, fetchRunPr]);

  useEffect(() => {
    if (discoveredPrUrl) fetchPrStatus(discoveredPrUrl);
  }, [discoveredPrUrl, fetchPrStatus]);

  useEffect(() => {
    if (prMerged && argoAppName) fetchArgoStatus();
  }, [prMerged, argoAppName, fetchArgoStatus]);

  // ─── PR URL parsing ───────────────────────────────────────────────────────────

  const prMatch  = effectivePrUrl?.match(/github\.com\/([^/]+)\/([^/]+)\/pull\/(\d+)/);
  const prNumber = prMatch?.[3];

  // ─── Stage helpers ────────────────────────────────────────────────────────────

  function prStageStatus(): StageStatus {
    if (!effectivePrUrl) return prLoading ? 'active' : 'pending';
    if (prMerged)   return 'done';
    if (prTimedOut) return 'timeout';
    if (prOpen)     return 'active';
    return 'active'; // URL exists, fetching status
  }

  function prStageDescription(): string {
    if (!effectivePrUrl) {
      if (actionsRunUrl) {
        if (runSuccess && discoveredPrUrl === undefined) return 'Scanning workflow logs for the pull request URL…';
        if (discoveredPrUrl === null) return 'No pull request found in workflow logs.';
        return 'A pull request will be opened once the workflow completes.';
      }
      return 'The template will open a pull request in the GitOps repository once it completes.';
    }
    if (prMerged) return `PR #${prNumber} was merged${prStatus?.mergedAt ? ` on ${new Date(prStatus.mergedAt).toLocaleString()}` : ''}.`;
    if (prTimedOut) {
      return `PR #${prNumber} has been open for over 10 minutes. Please wait for the CCP team to review and merge it, or drop a note in the #openshift Slack channel.`;
    }
    if (prOpen) return `PR #${prNumber} is open and awaiting review by the CCP team. It will be auto-detected once merged.`;
    return `PR #${prNumber} found — fetching status…`;
  }

  function argoStageStatus(): StageStatus {
    if (!prMerged) return 'pending';
    if (!argoAppName) return 'active'; // no app name — show generic active
    if (!argoStatus) return 'active';
    if (!argoStatus.found) return 'active';
    if (argoFailed) return 'failed';
    if (argoSynced && argoHealthy) return 'done';
    return 'active';
  }

  function argoStageDescription(): string {
    if (!prMerged) return 'ArgoCD watches the GitOps repository and automatically syncs after the PR is merged (selfHeal + prune enabled).';
    if (!argoAppName) return 'ArgoCD detected the merged PR and is syncing the new configuration to the cluster.';
    if (!argoStatus || !argoStatus.found) return `Waiting for ArgoCD application "${argoAppName}" to appear…`;
    const sync   = argoStatus.syncStatus   ?? 'Unknown';
    const health = argoStatus.healthStatus ?? 'Unknown';
    const ts     = argoStatus.lastSyncTime ? ` (last sync: ${new Date(argoStatus.lastSyncTime).toLocaleString()})` : '';
    if (argoFailed)           return `ArgoCD sync issue detected — Sync: ${sync}, Health: ${health}${ts}. Check the ArgoCD application for details.`;
    if (argoSynced && argoHealthy) return `ArgoCD application "${argoAppName}" is Synced and Healthy${ts}.`;
    return `ArgoCD application "${argoAppName}" — Sync: ${sync}, Health: ${health}${ts}. Waiting for Synced + Healthy…`;
  }

  function resourcesStageStatus(): StageStatus {
    if (!prMerged) return 'pending';
    if (argoAppName) return (argoSynced && argoHealthy) ? 'done' : 'active';
    return 'active'; // generic: assume active after merge
  }

  // ─── Stage definitions ────────────────────────────────────────────────────────

  const stages: PipelineStage[] = [];

  // Stage 1: Template executed
  stages.push({
    label: 'Template executed',
    description: templateFailed
      ? 'The Backstage template failed. Check the execution steps above for details.'
      : templateActive
      ? 'The Backstage template is running…'
      : 'The Backstage template ran successfully.',
    status: templateFailed ? 'failed' : templateActive ? 'active' : templateDone ? 'done' : 'pending',
  });

  // Stage 2: GitHub Actions (only when a workflow was dispatched)
  if (actionsRunUrl) {
    stages.push({
      label: 'GitHub Actions triggered',
      description: 'The GitHub Actions workflow was triggered by the template.',
      status: actionsRunUrl ? 'done' : !templateDone ? 'pending' : 'active',
      links: [{ label: 'View workflow run', url: actionsRunUrl }],
    });

    const workflowStatus: StageStatus = runLoading ? 'active'
      : runFailed   ? 'failed'
      : runSuccess  ? 'done'
      : runInFlight ? 'active'
      : runStatus   ? 'active'
      : 'pending';

    stages.push({
      label: 'GitHub Actions workflow',
      description: runFailed
        ? 'The GitHub Actions workflow did not complete successfully.'
        : runSuccess
        ? 'The workflow completed successfully.'
        : runInFlight
        ? `Workflow "${runStatus?.name ?? 'workflow'}" is running…`
        : 'Waiting for workflow status…',
      status: workflowStatus,
      links: [{ label: runStatus?.name ?? 'View workflow run', url: actionsRunUrl }],
    });
  }

  // Stage 3: PR created & reviewed
  const prLinks: PipelineLink[] = [];
  if (effectivePrUrl) {
    prLinks.push({ label: prMerged ? `PR #${prNumber} merged` : `Review PR #${prNumber}`, url: effectivePrUrl });
  }
  stages.push({
    label: 'Pull Request reviewed & merged',
    description: prStageDescription(),
    status: prStageStatus(),
    links: prLinks.length ? prLinks : undefined,
  });

  // Stage 4 & 5: ArgoCD sync + Resources live — only when an app name is configured
  if (argoAppName) {
    const argoLinks: PipelineLink[] = [];
    if (argoStatus?.appUrl) {
      argoLinks.push({ label: `ArgoCD: ${argoAppName}`, url: argoStatus.appUrl });
    } else if (entityRef) {
      argoLinks.push({ label: 'View entity ArgoCD', url: `/catalog/${entityRef}/argocd` });
    }
    stages.push({
      label: 'ArgoCD detects & syncs',
      description: argoStageDescription(),
      status: argoStageStatus(),
      links: argoLinks.length ? argoLinks : undefined,
    });

    stages.push({
      label: `Resources live in ${platform}`,
      description: (argoSynced && argoHealthy)
        ? 'Namespaces, RBAC, network policies, and application resources are created and running on the cluster.'
        : 'Resources will be live once ArgoCD finishes syncing.',
      status: resourcesStageStatus(),
    });
  }

  const isPolling = needRunPoll || needRunPrPoll || needPrPoll || needArgoPoll;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: S.radius, boxShadow: S.shadow, overflow: 'hidden' }}>
      {/* Header */}
      <Box px={2} py={1.5} display="flex" alignItems="center" style={{ borderBottom: `1px solid ${S.border}` }}>
        <Typography style={{ color: S.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Deployment Pipeline
        </Typography>
        <Box ml="auto" display="flex" alignItems="center" gap={0.75}>
          {isPolling && (
            <>
              <CircularProgress size={11} style={{ color: S.textMuted }} />
              <Typography style={{ color: S.textMuted, fontSize: 11 }}>auto-refresh 15s</Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Vertical timeline */}
      <Box px={2} py={1} style={{ position: 'relative' }}>
        {stages.length > 1 && (
          <Box style={{ position: 'absolute', left: 25, top: 28, bottom: 28, width: 2, background: S.border, zIndex: 0 }} />
        )}
        {stages.map((stage, idx) => (
          <Box key={stage.label} display="flex" gap={1.5} style={{ position: 'relative', zIndex: 1, padding: '10px 0', borderBottom: idx < stages.length - 1 ? `1px solid ${S.border}` : 'none' }}>
            {/* Dot on a white chip so it masks the connecting line */}
            <Box style={{ background: S.white, borderRadius: '50%', display: 'flex', alignItems: 'center', flexShrink: 0, height: 20 }}>
              <StageDot status={stage.status} />
            </Box>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1} style={{ minHeight: 20 }}>
                <Typography style={{ color: S.text, fontSize: 14, fontWeight: 600 }}>{stage.label}</Typography>
                <Box ml="auto" flexShrink={0}><StagePill status={stage.status} /></Box>
              </Box>
              <Typography style={{ color: S.textSub, fontSize: 12.5, lineHeight: 1.5, marginTop: 2 }}>
                {stage.description}
              </Typography>
              {stage.links && (
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {stage.links.map(link => <StageLink key={link.url} link={link} />)}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
